import { startTransition, useState } from "react";
import TableChartIcon from "@mui/icons-material/TableChart";
import type { RaRecord } from "react-admin";
import {
  Button,
  useDataProvider,
  useListContext,
  useNotify,
  useResourceContext,
  type ButtonProps,
} from "react-admin";
import TallyExportPreviewDialog from "@/components/TallyExportPreviewDialog";
import { useTallyConfig } from "@/hooks/useTallyConfig";
import { isLagatTallyConfigReady, type TallyConfig } from "@/types/tally-config";
import {
  buildTallyExportPreview,
  type TallyExportModule,
  type TallyExportPreview,
} from "@/utils/export-to-tally-excel";
import { fetchAllListRows } from "@/utils/fetch-all-list-rows";
import { getListExportFilter } from "@/utils/list-export-filter";
import { getStoredNarrationOptions } from "@/utils/tally-narration-options";

type TallyExportButtonProps = Omit<ButtonProps, "onClick" | "label"> & {
  module: TallyExportModule;
  filenamePrefix: string;
  isConfigReady?: (config: TallyConfig) => boolean;
  label?: string;
};

export default function TallyExportButton({
  module,
  filenamePrefix,
  isConfigReady = isLagatTallyConfigReady,
  disabled = false,
  label = "Export for Tally",
  size = "small",
  ...rest
}: TallyExportButtonProps) {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const resourceFromContext = useResourceContext();
  const { filter, filterValues, sort, resource: listResource } = useListContext();
  const listFilter = getListExportFilter(
    filterValues as Record<string, unknown> | undefined,
    filter as Record<string, unknown> | undefined
  );
  const resource = listResource ?? resourceFromContext;
  const { fetchConfig } = useTallyConfig();
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preview, setPreview] = useState<TallyExportPreview | null>(null);
  const [dialogConfig, setDialogConfig] = useState<TallyConfig | null>(null);
  const [dialogRecords, setDialogRecords] = useState<RaRecord[]>([]);
  const [totalNote, setTotalNote] = useState<string | undefined>();

  const handleClick = async () => {
    if (disabled || busy) return;
    setBusy(true);
    try {
      const config = await fetchConfig();
      if (!config) {
        notify(
          "Could not load Tally config. Open Accounts → Setup → Tally config (needs view permission).",
          { type: "warning" }
        );
        return;
      }

      if (!resource) {
        notify("Cannot export: list resource is not available.", { type: "error" });
        return;
      }

      const records = (await fetchAllListRows(dataProvider, resource, {
        sort: sort ?? { field: "id", order: "DESC" },
        filter: listFilter,
      })) as RaRecord[];
      if (records.length === 0) {
        notify("No receipts to export for the current filters.", { type: "info" });
        return;
      }

      // Yield so "Loading…" paints before building/rendering a large preview.
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 0);
      });

      // Module readiness is advisory only — other modules' empty ledgers must not block export.
      const narrationOptions = getStoredNarrationOptions(module);
      const built = buildTallyExportPreview(module, records, config, {}, narrationOptions);
      const moduleReady = isConfigReady(config);

      startTransition(() => {
        setDialogConfig(config);
        setDialogRecords(records);
        setPreview(built);
        setTotalNote(undefined);
        setDialogOpen(true);
      });

      if (!moduleReady) {
        notify(
          "Ledger mappings for this receipt type look incomplete. Check skipped receipts, or update Accounts → Setup → Tally config.",
          { type: "warning" }
        );
      } else if (built.rows.length === 0 && built.skipped.length === 0) {
        notify("No Tally voucher rows could be built from these receipts.", { type: "warning" });
      }
    } catch (e) {
      console.error("Tally export failed:", e);
      notify("Tally export failed", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setPreview(null);
    setDialogConfig(null);
    setDialogRecords([]);
    setTotalNote(undefined);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={disabled || busy}
        label={busy ? "Loading…" : label}
        size={size}
        {...rest}
      >
        <TableChartIcon />
      </Button>
      {dialogOpen && preview && dialogConfig ? (
        <TallyExportPreviewDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          module={module}
          config={dialogConfig}
          records={dialogRecords}
          filenamePrefix={filenamePrefix}
          totalNote={totalNote}
          initialPreview={preview}
        />
      ) : null}
    </>
  );
}
